import { Button } from '@headlessui/react';
import { MoreVertical, AlignRight, LogOut } from 'lucide-react';
import { useContext, createContext, useState, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IoMdSettings } from 'react-icons/io';
import { PerformClientSideLogout } from '../../../utils/helpers';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { logout } from '../../../redux/slices/authSlice';

// Define the context type
interface SidebarContextType {
  expanded: boolean;
}

// Create context with proper typing
const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

// Sidebar component props interface - support both regular children and function children
interface SidebarProps {
  children: ReactNode | ((expanded: boolean) => ReactNode);
  onToggle?: (expanded: boolean) => void;
}

export default function Sidebar({ children, onToggle }: SidebarProps) {
  const [expanded, setExpanded] = useState<boolean>(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const handleToggle = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    onToggle?.(newExpanded);
  };

  const queryClient = useQueryClient();
  const handleLogout = () => {
    setIsLoggingOut(true);

    // Remove all queries from the cache
    queryClient.removeQueries();

    // Cancel any ongoing queries
    queryClient.cancelQueries();
    // Reset the query client to clear any in-memory cache
    queryClient.clear();
    dispatch(logout());

    setTimeout(() => {
      sessionStorage.removeItem('accessToken');
      navigate('/login');
    }, 500);
  };

  const location = useLocation();
  const SettingsIcon = IoMdSettings as unknown as React.FC<
    React.SVGProps<SVGSVGElement>
  >;

  return (
    <aside className="h-screen">
      <nav className="h-full flex flex-col bg-white border-r shadow-sm">
        <div
          className={`px-4 flex justify-between items-center border-b border-[#E5E7EB] mb-4 ${
            expanded ? 'py-[21.5px]' : 'py-[26px]'
          }`}
        >
          {expanded && (
            <Link to={'/dashboard'}>
              <div className="flex items-center gap-2">
                <img
                  src="/agency/assets/workfall-logo.svg"
                  alt="workfall logo"
                  className="h-[20px]"
                />
                <span className="logo-text text-lg mt-1 text-primary">
                  Workfall
                </span>
              </div>
            </Link>
          )}

          {!expanded ? (
            <button className="flex items-center gap-2" onClick={handleToggle}>
              <img
                src="/agency/assets/workfall-logo.svg"
                alt="workfall logo"
                className="h-[20px]"
              />
            </button>
          ) : (
            <button
              onClick={handleToggle}
              className="rounded-lg bg-gray-50 hover:bg-gray-100 p-1.5"
            >
              <AlignRight size={18} className="text-gray-600" />
            </button>
          )}
        </div>

        <SidebarContext.Provider value={{ expanded }}>
          <ul className="flex-1 px-2">
            {typeof children === 'function' ? children(expanded) : children}
          </ul>
        </SidebarContext.Provider>
        <div className="px-2">
          <Link
            to="/settings"
            className={`
          relative flex items-center py-2.5 px-2.5 my-1
          font-medium rounded-lg cursor-pointer
          transition-all duration-200 group
          ${
            location.pathname.includes('/settings')
              ? ' text-orange-600 bg-[#FF664633]'
              : 'hover:bg-orange-50 text-gray-600 hover:text-orange-500'
          }
        `}
          >
            <SettingsIcon className="w-5 h-5" />{' '}
            <div
              className={`
              flex justify-between items-center
              overflow-hidden transition-all ${expanded ? 'w-40 ml-2' : 'w-0'}
            `}
            >
              Settings
            </div>
          </Link>
          <Button
            className="flex p-3 hover:text-red-500"
            onClick={handleLogout}
            disabled={isLoggingOut}
          >
            <LogOut className="w-5 h-5 text-gray-600 " />
            <div
              className={`
              flex justify-between items-center
              overflow-hidden text-gray-600 font-medium transition-all ${expanded ? 'w-40 ml-2' : 'w-0'}
            `}
            >
              {isLoggingOut ? 'Logging out...' : 'Logout'}
            </div>
          </Button>
        </div>
      </nav>
    </aside>
  );
}

interface SidebarItemProps {
  icon: ReactNode;
  activeIcon: ReactNode;
  hoverIcon: ReactNode;
  text: string;
  path: string;
  alert?: boolean;
}

export function SidebarItem({
  icon,
  activeIcon,
  hoverIcon,
  text,
  path,
  alert = false,
}: SidebarItemProps) {
  const context = useContext(SidebarContext);
  const location = useLocation();

  // Handle case where context might be undefined
  if (!context) {
    throw new Error('SidebarItem must be used within a Sidebar component');
  }

  const { expanded } = context;
  const isActive = location.pathname === path;

  return (
    <li className="relative group">
      <Link
        to={path}
        className={`
          relative flex items-center py-2.5 px-2.5 my-1
          font-medium rounded-lg cursor-pointer
          transition-all duration-200 group
          ${
            isActive
              ? ' text-orange-600 bg-[#FF664633]'
              : 'hover:bg-orange-50 text-gray-600 hover:text-orange-500'
          }
        `}
      >
        {/* Icon with conditional rendering based on state */}
        <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
          {isActive ? (
            activeIcon
          ) : (
            <>
              <span className="group-hover:hidden">{icon}</span>
              <span className="hidden group-hover:block">{hoverIcon}</span>
            </>
          )}
        </div>

        <span
          className={`overflow-hidden transition-all whitespace-nowrap text-sm ${
            expanded ? 'w-40 ml-2' : 'w-0'
          }`}
        >
          {text}
        </span>

        {alert && (
          <div
            className={`absolute right-2 w-2 h-2 rounded-full bg-orange-400 ${
              expanded ? '' : 'top-2'
            }`}
          />
        )}

        {!expanded && (
          <div
            className={`
              absolute left-full rounded-md px-2 py-1 ml-6
              bg-gray-800 text-white text-sm z-50
              invisible opacity-0 -translate-x-3 transition-all
              group-hover:visible group-hover:opacity-100 group-hover:translate-x-0
              whitespace-nowrap
            `}
          >
            {text}
          </div>
        )}
      </Link>
    </li>
  );
}

// FixedSidebarItem - version that doesn't require context (for fixed sidebar)
interface FixedSidebarItemProps {
  icon: ReactNode;
  activeIcon: ReactNode;
  hoverIcon: ReactNode;
  text: string;
  path: string;
  expanded: boolean;
  alert?: boolean;
}

export function FixedSidebarItem({
  icon,
  activeIcon,
  hoverIcon,
  text,
  path,
  expanded,
  alert = false,
}: FixedSidebarItemProps) {
  const location = useLocation();
  const isActive = location.pathname.includes(path);

  return (
    <li className="relative group">
      <Link
        to={path}
        className={`
          relative flex items-center py-2.5 px-2.5 my-1
          font-medium rounded-lg cursor-pointer
          transition-all duration-200 group
          ${
            isActive
              ? ' text-orange-600 bg-[#FF664633]'
              : 'hover:bg-orange-50 text-gray-600 hover:text-orange-500'
          }
        `}
      >
        {/* Icon with conditional rendering based on state */}
        <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
          {isActive ? (
            activeIcon
          ) : (
            <>
              <span className="group-hover:hidden">{icon}</span>
              <span className="hidden group-hover:block">{hoverIcon}</span>
            </>
          )}
        </div>

        <span
          className={`overflow-hidden transition-all whitespace-nowrap text-sm ${
            expanded ? 'w-40 ml-2' : 'w-0'
          }`}
        >
          {text}
        </span>

        {alert && (
          <div
            className={`absolute right-2 w-2 h-2 rounded-full bg-orange-400 ${
              expanded ? '' : 'top-2'
            }`}
          />
        )}

        {!expanded && (
          <div
            className={`
              absolute left-full rounded-md px-2 py-1 ml-6
              bg-gray-800 text-white text-sm z-50
              invisible opacity-0 -translate-x-3 transition-all
              group-hover:visible group-hover:opacity-100 group-hover:translate-x-0
              whitespace-nowrap
            `}
          >
            {text}
          </div>
        )}
      </Link>
    </li>
  );
}

export function useSidebar(): SidebarContextType {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a Sidebar component');
  }
  return context;
}
